import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Flag, ChevronLeft, ChevronRight, Send, Clock, Volume2, VolumeX } from "lucide-react";

// Question palette states: not-visited | attempted | marked | attempted-marked | not-answered
function paletteStyle(state) {
  const map = {
    "not-visited": "bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500",
    current: "bg-orange-600 border-orange-500 text-white ring-2 ring-orange-400/40",
    attempted: "bg-emerald-600 border-emerald-500 text-white",
    marked: "bg-amber-500 border-amber-400 text-zinc-900",
    "attempted-marked": "bg-amber-500 border-amber-400 text-zinc-900 ring-2 ring-emerald-500/50",
    "not-answered": "bg-rose-600 border-rose-500 text-white",
  };
  return map[state] || map["not-visited"];
}

export default function ExamSimulation() {
  const { examId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [attemptId, setAttemptId] = useState(searchParams.get("attempt"));
  const [exam, setExam] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [visited, setVisited] = useState({});
  const [marked, setMarked] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [voiceOn, setVoiceOn] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const telemetryBuf = useRef([]);
  const viewStart = useRef(Date.now());
  const announced = useRef({});

  // Init
  useEffect(() => {
    (async () => {
      let att = attemptId;
      if (!att) {
        const r = await api.post("/attempts/start", { exam_id: examId });
        att = r.data.attempt_id;
        setAttemptId(att);
        setExam(r.data.exam);
      } else {
        const r = await api.get(`/attempts/${att}`);
        setExam(r.data.exam);
        setAnswers(r.data.attempt.answers || {});
        setMarked(Object.fromEntries((r.data.attempt.marked_for_review || []).map((q) => [q, true])));
      }
      // request fullscreen
      try {
        await document.documentElement.requestFullscreen?.();
      } catch (err) {
        console.warn("fullscreen denied:", err?.message);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const questions = exam?.structure?.questions || [];
  const totalSec = (exam?.structure?.total_time_minutes || 60) * 60;
  const remaining = Math.max(0, totalSec - elapsed);

  // Timer
  useEffect(() => {
    if (!exam) return;
    const iv = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(iv);
  }, [exam]);

  // Voice announcements
  const speak = useCallback((text) => {
    if (!voiceOn || !("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1;
    u.volume = 0.9;
    window.speechSynthesis.speak(u);
  }, [voiceOn]);

  useEffect(() => {
    if (!exam || remaining <= 0) return;
    const min = Math.floor(remaining / 60);
    const marks = [30, 15, 10, 5, 1];
    for (const m of marks) {
      if (remaining === m * 60 && !announced.current[m]) {
        announced.current[m] = true;
        speak(`${m} minute${m > 1 ? "s" : ""} remaining`);
      }
    }
    if (remaining === 0 && !announced.current.done) {
      announced.current.done = true;
      speak("Time is up. Submitting.");
      doSubmit(true);
    }
    // eslint-disable-next-line
  }, [remaining, exam]);

  // Telemetry: record view on question change
  useEffect(() => {
    if (!questions.length) return;
    const q = questions[current];
    if (!q) return;
    viewStart.current = Date.now();
    pushEvent({ q_id: q.q_id, event_type: "view", timestamp: new Date().toISOString() });
    setVisited((v) => ({ ...v, [q.q_id]: true }));
    return () => {
      const dur = Date.now() - viewStart.current;
      pushEvent({
        q_id: q.q_id,
        event_type: "view",
        timestamp: new Date().toISOString(),
        duration_ms: dur,
      });
    };
    // eslint-disable-next-line
  }, [current, questions.length]);

  // Flush telemetry
  useEffect(() => {
    const iv = setInterval(async () => {
      if (!attemptId || telemetryBuf.current.length === 0) return;
      const batch = telemetryBuf.current.splice(0, telemetryBuf.current.length);
      try {
        await api.post(`/attempts/${attemptId}/telemetry`, batch);
      } catch (err) {
        console.error("telemetry flush failed:", err);
      }
    }, 8000);
    return () => clearInterval(iv);
  }, [attemptId]);

  const pushEvent = (e) => telemetryBuf.current.push(e);

  // For MCQ (single click = one meaningful change), emit event immediately.
  const setAns = (qid, val) => {
    setAnswers((a) => {
      const prev = a[qid];
      pushEvent({
        q_id: qid,
        event_type: prev ? "change" : "answer",
        timestamp: new Date().toISOString(),
        previous_value: prev || null,
        new_value: val,
      });
      return { ...a, [qid]: val };
    });
  };

  // For multi-select MCQ: toggle a letter in a sorted comma-joined string.
  const toggleMultiAns = (qid, letter) => {
    setAnswers((a) => {
      const prev = a[qid] || "";
      const set = new Set(prev.split(",").map((s) => s.trim()).filter(Boolean));
      if (set.has(letter)) set.delete(letter);
      else set.add(letter);
      const nextVal = Array.from(set).sort().join(",");
      pushEvent({
        q_id: qid,
        event_type: prev ? "change" : "answer",
        timestamp: new Date().toISOString(),
        previous_value: prev || null,
        new_value: nextVal || null,
      });
      const n = { ...a };
      if (nextVal) n[qid] = nextVal; else delete n[qid];
      return n;
    });
  };

  // For text/integer, update state on every keystroke but emit ONE event on blur.
  const textFocusValueRef = useRef({});
  const setTextAns = (qid, val) => {
    setAnswers((a) => ({ ...a, [qid]: val }));
  };
  const onTextFocus = (qid, val) => {
    textFocusValueRef.current[qid] = val || "";
  };
  const flushTextChange = (qid, finalVal) => {
    const before = textFocusValueRef.current[qid] ?? "";
    const after = finalVal ?? "";
    if (before === after) return;
    pushEvent({
      q_id: qid,
      event_type: before ? "change" : "answer",
      timestamp: new Date().toISOString(),
      previous_value: before || null,
      new_value: after || null,
    });
    textFocusValueRef.current[qid] = after;
  };

  const toggleMark = (qid) => {
    setMarked((m) => {
      const on = !m[qid];
      pushEvent({ q_id: qid, event_type: on ? "mark_review" : "unmark_review", timestamp: new Date().toISOString() });
      return { ...m, [qid]: on };
    });
  };

  const clearAns = (qid) => {
    pushEvent({ q_id: qid, event_type: "skip", timestamp: new Date().toISOString(), previous_value: answers[qid], new_value: null });
    setAnswers((a) => {
      const n = { ...a };
      delete n[qid];
      return n;
    });
  };

  const stateOf = (qid, idx) => {
    if (idx === current) return "current";
    const ans = answers[qid];
    const mk = marked[qid];
    if (ans && mk) return "attempted-marked";
    if (ans) return "attempted";
    if (mk) return "marked";
    if (visited[qid]) return "not-answered";
    return "not-visited";
  };

  const doSubmit = async (auto = false) => {
    if (submitting) return;
    setSubmitting(true);
    // final telemetry flush
    const q = questions[current];
    if (q) {
      pushEvent({
        q_id: q.q_id,
        event_type: "view",
        timestamp: new Date().toISOString(),
        duration_ms: Date.now() - viewStart.current,
      });
    }
    const batch = telemetryBuf.current.splice(0);
    try {
      await api.post(`/attempts/${attemptId}/submit`, {
        answers,
        marked_for_review: Object.keys(marked).filter((k) => marked[k]),
        telemetry_batch: batch,
        time_used_seconds: elapsed,
      });
      try { await document.exitFullscreen?.(); } catch (err) { console.warn("exit fullscreen failed:", err?.message); }
      toast.success(auto ? "Time up. Submitted." : "Submitted.");
      navigate(`/results/${attemptId}`);
    } catch (e) {
      toast.error("Submit failed");
      setSubmitting(false);
    }
  };

  if (!exam) {
    return (
      <div className="exam-grain min-h-screen flex items-center justify-center text-zinc-300 font-mono text-sm">
        Loading exam…
      </div>
    );
  }

  const q = questions[current];
  const attemptedCount = Object.values(answers).filter(Boolean).length;
  const markedCount = Object.values(marked).filter(Boolean).length;

  return (
    <div className="exam-grain min-h-screen text-zinc-100 dark">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2 rounded-full bg-orange-500 animate-pulse" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Exam in progress</div>
              <div className="font-heading text-base font-semibold">{exam.structure.title}</div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 font-mono tabular-nums">
              <Clock className="h-4 w-4 text-orange-500" />
              <span className={`text-2xl font-semibold ${remaining < 300 ? "text-rose-500" : "text-zinc-100"}`}>
                {fmt(remaining)}
              </span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setVoiceOn((v) => !v)} className="text-zinc-400 hover:text-zinc-100" data-testid="voice-toggle">
              {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
            <Button
              onClick={() => doSubmit(false)}
              disabled={submitting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="submit-exam-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Submit
            </Button>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-65px)]">
        {/* Question area */}
        <main className="flex-1 overflow-y-auto p-8 lg:p-12">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Badge variant="outline" className="border-zinc-700 text-zinc-400 font-mono text-xs">
                {q.section}
              </Badge>
              <Badge variant="outline" className="border-zinc-700 text-zinc-400 font-mono text-xs">
                {q.q_type.toUpperCase()}
              </Badge>
              <span className="font-mono text-xs text-zinc-500">
                +{q.marks_correct} / {q.marks_wrong}
              </span>
            </div>
            <div className="flex items-baseline gap-4 mb-6">
              <span className="font-mono text-4xl font-bold text-zinc-500 tabular-nums">
                {String(q.number).padStart(2, "0")}
              </span>
              <p className="font-heading text-xl leading-relaxed text-zinc-50">{q.text}</p>
            </div>

            {q.q_type === "mcq" ? (
              <div className="space-y-2" data-testid="options">
                {q.options.map((opt, i) => {
                  const letter = String.fromCharCode(65 + i);
                  const selected = answers[q.q_id] === letter;
                  return (
                    <button
                      key={`${q.q_id}-opt-${letter}`}
                      onClick={() => setAns(q.q_id, letter)}
                      data-testid={`option-${letter}`}
                      className={`w-full text-left p-4 rounded-md border transition-colors ${
                        selected
                          ? "border-orange-500 bg-orange-500/10 text-zinc-50"
                          : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-600 hover:bg-zinc-900"
                      }`}
                      style={{ transition: "border-color 0.15s ease, background-color 0.15s ease" }}
                    >
                      <div className="flex items-center gap-4">
                        <span
                          className={`flex h-8 w-8 items-center justify-center rounded font-mono text-sm font-semibold ${
                            selected ? "bg-orange-600 text-white" : "bg-zinc-800 text-zinc-400"
                          }`}
                        >
                          {letter}
                        </span>
                        <span className="flex-1">{opt}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : q.q_type === "mcq_multi" ? (
              <div className="space-y-2" data-testid="options-multi">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-400 mb-1">
                  Select all that apply
                </div>
                {q.options.map((opt, i) => {
                  const letter = String.fromCharCode(65 + i);
                  const selectedSet = new Set((answers[q.q_id] || "").split(",").filter(Boolean));
                  const selected = selectedSet.has(letter);
                  return (
                    <button
                      key={`${q.q_id}-multi-${letter}`}
                      onClick={() => toggleMultiAns(q.q_id, letter)}
                      data-testid={`option-multi-${letter}`}
                      className={`w-full text-left p-4 rounded-md border transition-colors ${
                        selected
                          ? "border-orange-500 bg-orange-500/10 text-zinc-50"
                          : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-zinc-600 hover:bg-zinc-900"
                      }`}
                      style={{ transition: "border-color 0.15s ease, background-color 0.15s ease" }}
                    >
                      <div className="flex items-center gap-4">
                        <span
                          className={`flex h-6 w-6 items-center justify-center rounded-sm border-2 font-mono text-[11px] font-semibold ${
                            selected ? "bg-orange-600 border-orange-600 text-white" : "border-zinc-600 text-zinc-500"
                          }`}
                        >
                          {selected ? "✓" : ""}
                        </span>
                        <span className="font-mono text-xs text-zinc-500 w-4">{letter}.</span>
                        <span className="flex-1">{opt}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div>
                <input
                  type={q.q_type === "integer" ? "number" : "text"}
                  className="w-full px-4 py-3 rounded-md bg-zinc-900 border border-zinc-800 text-lg font-mono text-zinc-50 focus:border-orange-500 focus:outline-none"
                  placeholder={q.q_type === "integer" ? "Enter a number" : "Type your answer"}
                  value={answers[q.q_id] || ""}
                  onFocus={(e) => onTextFocus(q.q_id, e.target.value)}
                  onChange={(e) => setTextAns(q.q_id, e.target.value)}
                  onBlur={(e) => flushTextChange(q.q_id, e.target.value)}
                  data-testid="answer-input"
                />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-8 pt-6 border-t border-zinc-800">
              <Button
                variant="outline"
                onClick={() => {
                  const cq = questions[current];
                  if (cq && (cq.q_type === "integer" || cq.q_type === "one_word"))
                    flushTextChange(cq.q_id, answers[cq.q_id] || "");
                  setCurrent((c) => Math.max(0, c - 1));
                }}
                disabled={current === 0}
                className="border-zinc-700 text-zinc-100 hover:bg-zinc-800"
                data-testid="prev-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => toggleMark(q.q_id)}
                className={`border-zinc-700 ${marked[q.q_id] ? "bg-amber-500 text-zinc-900 hover:bg-amber-500" : "text-zinc-100 hover:bg-zinc-800"}`}
                data-testid="mark-review-btn"
              >
                <Flag className="h-4 w-4 mr-1" /> {marked[q.q_id] ? "Marked" : "Mark for review"}
              </Button>
              <Button
                variant="outline"
                onClick={() => clearAns(q.q_id)}
                className="border-zinc-700 text-zinc-100 hover:bg-zinc-800"
                data-testid="clear-btn"
              >
                Clear
              </Button>
              <div className="flex-1" />
              {current === questions.length - 1 ? (
                <Button
                  onClick={() => {
                    // ensure any pending text change is flushed
                    const cq = questions[current];
                    if (cq && (cq.q_type === "integer" || cq.q_type === "one_word")) {
                      flushTextChange(cq.q_id, answers[cq.q_id] || "");
                    }
                    doSubmit(false);
                  }}
                  disabled={submitting}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="last-submit-btn"
                >
                  <Send className="h-4 w-4 mr-1" /> Submit exam
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    const cq = questions[current];
                    if (cq && (cq.q_type === "integer" || cq.q_type === "one_word")) {
                      flushTextChange(cq.q_id, answers[cq.q_id] || "");
                    }
                    setCurrent((c) => Math.min(questions.length - 1, c + 1));
                  }}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="next-btn"
                >
                  Save & Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </main>

        {/* Palette */}
        <aside className="w-80 border-l border-zinc-800 bg-zinc-950 overflow-y-auto">
          <div className="p-5 border-b border-zinc-800">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Overview</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat n={attemptedCount} label="Attempted" color="text-emerald-400" />
              <Stat n={markedCount} label="Marked" color="text-amber-400" />
              <Stat n={questions.length - attemptedCount} label="Left" color="text-rose-400" />
            </div>
          </div>
          <div className="p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3">Question palette</div>
            <div className="grid grid-cols-5 gap-2" data-testid="question-palette">
              {questions.map((qq, i) => (
                <button
                  key={qq.q_id}
                  onClick={() => {
                    const cq = questions[current];
                    if (cq && (cq.q_type === "integer" || cq.q_type === "one_word"))
                      flushTextChange(cq.q_id, answers[cq.q_id] || "");
                    setCurrent(i);
                  }}
                  data-testid={`palette-${i}`}
                  className={`h-10 w-full rounded border font-mono text-xs font-semibold transition-colors ${paletteStyle(stateOf(qq.q_id, i))}`}
                  style={{ transition: "background-color 0.15s ease, border-color 0.15s ease" }}
                >
                  {qq.number}
                </button>
              ))}
            </div>
            <div className="mt-6 space-y-2 text-xs text-zinc-400">
              <LegendItem cls="bg-emerald-600" label="Attempted" />
              <LegendItem cls="bg-amber-500" label="Marked for review" />
              <LegendItem cls="bg-rose-600" label="Visited, not answered" />
              <LegendItem cls="bg-zinc-900 border border-zinc-700" label="Not visited" />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function fmt(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const p = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${p(h)}:${p(m)}:${p(s)}` : `${p(m)}:${p(s)}`;
}

function Stat({ n, label, color }) {
  return (
    <div>
      <div className={`font-mono text-2xl font-semibold tabular-nums ${color}`}>{n}</div>
      <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500 mt-1">{label}</div>
    </div>
  );
}
function LegendItem({ cls, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`h-4 w-4 rounded ${cls}`} />
      <span>{label}</span>
    </div>
  );
}
